import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Modal,
} from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import axios from 'axios';
import InventoryCategoryManager from '../../components/InventoryCategoryManager';

const API_VENDOR_PRODUCTS = 'https://figazuresql.swancreate.com/api/swa/FINANCE_VendorProduct';
const API_DISTRICTS = 'https://figazuresql.swancreate.com/api/swa/SchoolDistrict';
const API_SITES = 'https://figazuresql.swancreate.com/api/swa/SchoolMst';
const API_COUNTS = 'https://figazuresql.swancreate.com/api/swa/FINANCE_InventoryCounts';
const API_INVENTORY_CATEGORIES = 'https://figazuresql.swancreate.com/api/swa/FINANCE_InventoryCategories';

const InventoryCountEntry = () => {
    const [vendors, setVendors] = useState([]);
    const [selectedVendor, setSelectedVendor] = useState('');
    const [districts, setDistricts] = useState([]);
    const [selectedDistrict, setSelectedDistrict] = useState('');
    const [sites, setSites] = useState([]);
    const [selectedSite, setSelectedSite] = useState('');
    const [allProducts, setAllProducts] = useState([]);
    const [rows, setRows] = useState([]);
    const [inventoryCategories, setInventoryCategories] = useState([]);
    const [isCategoryManagerOpen, setIsCategoryManagerOpen] = useState(false);

    // load vendors, districts, categories once
    useEffect(() => {
        fetchVendors();
        fetchDistricts();
        fetchInventoryCategories();
    }, []);

    // reload sites on district change
    useEffect(() => {
        if (selectedDistrict) fetchSites();
    }, [selectedDistrict]);

    // rebuild rows on vendor/district/allProducts change
    useEffect(() => {
        if (!selectedVendor || !selectedDistrict) {
            setRows([]);
            return;
        }
        const filtered = allProducts.filter(
            p =>
                p.VendorId === selectedVendor &&
                p.SchoolDistrictID === selectedDistrict &&
                p.isActive
        );
        setRows(
            filtered.map(p => ({
                id: p.apiid,
                ProductID: p.ProductID,
                FIGEXID: p.FIGEXID,
                ProductName: p.ProductName,
                LowUnitNumber: p.LowUnitNumber,
                UnitCost: p.UnitPrice,
                InventoryCategoryID: p.InventoryCategoryID || '',
                InventoryCategoryName: p.InventoryCategoryName || '',
                ProcessID: p.ProcessID,
                VendorID: p.VendorId,
                VendorName: p.VendorName,
                UNITS: 0,
                PIECES: 0,
            }))
        );
    }, [allProducts, selectedVendor, selectedDistrict]);

    async function fetchVendors() {
        try {
            const { data } = await axios.get(API_VENDOR_PRODUCTS);
            setAllProducts(data);
            const map = {};
            data.forEach(p => {
                if (p.VendorId && p.VendorName) map[p.VendorId] = p.VendorName;
            });
            setVendors(Object.entries(map).map(([VendorId, VendorName]) => ({ VendorId, VendorName })));
        } catch (e) {
            console.error('Failed loading vendor products:', e);
        }
    }

    async function fetchDistricts() {
        try {
            const { data } = await axios.get(API_DISTRICTS);
            setDistricts(data);
        } catch (e) {
            console.error('Failed loading districts:', e);
        }
    }

    async function fetchSites() {
        try {
            const { data } = await axios.get(API_SITES);
            setSites(data.filter(s => s.SchoolDistrictId === selectedDistrict));
        } catch (e) {
            console.error('Failed loading sites:', e);
        }
    }

    async function fetchInventoryCategories() {
        try {
            const { data } = await axios.get(API_INVENTORY_CATEGORIES);
            setInventoryCategories(
                data.map(c => ({
                    InventoryCategoryID: c.InventoryCategoryID,
                    InventoryCategoryName: c.InventoryCatgegoryName, // table typo
                }))
            );
        } catch (e) {
            console.error('Failed loading inventory categories:', e);
        }
    }

    const handleEdit = params => {
        const { id, field, value } = params;
        setRows(prev =>
            prev.map(r => {
                if (r.id !== id) return r;
                if (field === 'InventoryCategoryID') {
                    const sel = inventoryCategories.find(c => c.InventoryCategoryID === value);
                    return {
                        ...r,
                        InventoryCategoryID: value,
                        InventoryCategoryName: sel ? sel.InventoryCategoryName : '',
                    };
                }
                return { ...r, [field]: value };
            })
        );
    };

    const handleSubmit = async () => {
        if (!selectedSite) {
            alert('Please pick a specific Site before submitting.');
            return;
        }
        const districtObj = districts.find(d => d.SchoolDistrictId === selectedDistrict);
        const siteObj = sites.find(s => s.SchoolId === selectedSite);
        const vendorObj = vendors.find(v => v.VendorId === selectedVendor);

        const toPost = rows
            .filter(r => r.UNITS > 0 || r.PIECES > 0)
            .map(r => ({
                DistrictID: selectedDistrict,
                DistrictName: districtObj?.DistrictName,
                SiteID: selectedSite,
                SiteName: siteObj?.SchoolName,
                ProductName: r.ProductName,
                ProductID: r.ProductID,
                FIGEXID: r.FIGEXID,
                UNITS: r.UNITS,
                PIECES: r.PIECES,
                UnitCost: r.UnitCost,
                TotalValue: r.UNITS * r.UnitCost,
                LowUnitNumber: r.LowUnitNumber,
                VendorID: vendorObj?.VendorId,
                VendorName: vendorObj?.VendorName,
                ProcessID: r.ProcessID,
                InventoryCategoryID: r.InventoryCategoryID,
                InventoryCategoryName: r.InventoryCategoryName,
            }));

        if (!toPost.length) {
            alert('No counts entered to submit.');
            return;
        }

        try {
            await Promise.all(toPost.map(body => axios.post(API_COUNTS, body)));
            alert('✅ Counts submitted!');
            setRows(prev => prev.map(r => ({ ...r, UNITS: 0, PIECES: 0 })));
        } catch (e) {
            console.error('Error saving counts:', e);
            alert('❌ Failed to submit counts.');
        }
    };

    const columns = [
        { field: 'ProductName', headerName: 'Product', flex: 2 },
        {
            field: 'InventoryCategoryID',
            headerName: 'Inventory Category',
            flex: 1,
            editable: true,
            type: 'singleSelect',
            valueOptions: inventoryCategories.map(c => ({
                value: c.InventoryCategoryID,
                label: c.InventoryCategoryName,
            })),
            valueFormatter: ({ value }) => {
                const cat = inventoryCategories.find(c => c.InventoryCategoryID === value);
                return cat ? cat.InventoryCategoryName : '';
            },
        },
        {
            field: 'UNITS',
            headerName: 'Units',
            type: 'number',
            editable: true,
            flex: 0.5,
        },
        {
            field: 'PIECES',
            headerName: 'Pieces',
            type: 'number',
            editable: true,
            flex: 0.5,
        },
        {
            field: 'UnitCost',
            headerName: 'Unit Cost',
            type: 'number',
            flex: 0.5,
            valueFormatter: p =>
                new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
                    p.value || 0
                ),
        },
    ];

    return (
        <>
            <Modal
                open={isCategoryManagerOpen}
                onClose={() => setIsCategoryManagerOpen(false)}
                sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
            >
                <Box
                    sx={{
                        backgroundColor: '#fff',
                        p: 2,
                        borderRadius: 1,
                        width: 600,
                        maxHeight: '80vh',
                        overflow: 'auto',
                    }}
                >
                    <InventoryCategoryManager />
                </Box>
            </Modal>

            <Box
                m="20px"
                sx={{
                    backgroundColor: '#1A1A2E',
                    color: '#EFFFFD',
                    minHeight: '100vh',
                    p: 2,
                    borderRadius: '10px',
                    boxShadow: '0px 0px 30px 5px rgba(0,255,255,0.5)',
                }}
            >
                <Typography variant="h4" gutterBottom>
                    Vendor Inventory Count Entry
                </Typography>

                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Button
                        variant="contained"
                        onClick={() => setIsCategoryManagerOpen(true)}
                        sx={{
                            backgroundColor: 'green',
                            color: '#fff',
                            fontWeight: 'bold',
                            '&:hover': { backgroundColor: 'darkgreen' },
                        }}
                    >
                        Manage Categories
                    </Button>
                </Box>

                <Box mb={2} display="flex" flexWrap="wrap" gap={2} alignItems="center">
                    {/* Vendor */}
                    <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel sx={{ color: '#EFFFFD' }}>Vendor</InputLabel>
                        <Select
                            value={selectedVendor}
                            onChange={e => setSelectedVendor(e.target.value)}
                            label="Vendor"
                            sx={{
                                color: '#EFFFFD',
                                '.MuiOutlinedInput-notchedOutline': { borderColor: '#EFFFFD' },
                                '.MuiSvgIcon-root': { color: '#EFFFFD' },
                            }}
                        >
                            {vendors.map(v => (
                                <MenuItem key={v.VendorId} value={v.VendorId}>
                                    {v.VendorName}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* District */}
                    <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel sx={{ color: '#EFFFFD' }}>District</InputLabel>
                        <Select
                            value={selectedDistrict}
                            onChange={e => setSelectedDistrict(e.target.value)}
                            label="District"
                            sx={{
                                color: '#EFFFFD',
                                '.MuiOutlinedInput-notchedOutline': { borderColor: '#EFFFFD' },
                                '.MuiSvgIcon-root': { color: '#EFFFFD' },
                            }}
                        >
                            {districts.map(d => (
                                <MenuItem key={d.SchoolDistrictId} value={d.SchoolDistrictId}>
                                    {d.DistrictName}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Site */}
                    <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel sx={{ color: '#EFFFFD' }}>Site</InputLabel>
                        <Select
                            value={selectedSite}
                            onChange={e => setSelectedSite(e.target.value)}
                            label="Site"
                            sx={{
                                color: '#EFFFFD',
                                '.MuiOutlinedInput-notchedOutline': { borderColor: '#EFFFFD' },
                                '.MuiSvgIcon-root': { color: '#EFFFFD' },
                            }}
                        >
                            <MenuItem value="">All</MenuItem>
                            {sites.map(s => (
                                <MenuItem key={s.SchoolId} value={s.SchoolId}>
                                    {s.SchoolName}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        sx={{ height: 40 }}
                        disabled={!selectedVendor || !selectedDistrict || !selectedSite}
                    >
                        Submit Counts
                    </Button>
                </Box>

                <Box height={600} width="100%">
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        pageSize={20}
                        rowsPerPageOptions={[20, 50, 100]}
                        disableSelectionOnClick
                        onCellEditCommit={handleEdit}
                        components={{ Toolbar: GridToolbar }}
                        componentsProps={{
                            toolbar: { showQuickFilter: true, quickFilterProps: { debounceMs: 300 } },
                        }}
                        sx={{
                            '& .MuiDataGrid-root': { border: 'none', backgroundColor: '#0F3460', color: '#EFFFFD' },
                            '& .MuiDataGrid-cell': { borderBottom: '1px solid #00FFFF' },
                            '& .MuiDataGrid-columnHeaders': { backgroundColor: '#16213E', color: '#EFFFFD' },
                            '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: 'rgba(0,191,255,0.1)' },
                            '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: 'rgba(0,191,255,0.2)' },
                        }}
                    />
                </Box>
            </Box>
        </>
    );
};

export default InventoryCountEntry;
